import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty } from "antd";
import { getActiveCampaigns } from "../features/customers/customerSlice";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../styles/agentWhatsapp.css";

const AgentsActiveCampaigns = () => {
  const dispatch = useDispatch();

  const { activeCampaigns, activeCampaignsLoading, isLoading } = useSelector(
    (state) => state.customer
  );

  const [hasFetchedCampaigns, setHasFetchedCampaigns] = useState(
    Array.isArray(activeCampaigns) && activeCampaigns.length > 0
  );

  useEffect(() => {
    if (Array.isArray(activeCampaigns) && activeCampaigns.length > 0) {
      setHasFetchedCampaigns(true);
      return;
    }

    const fetchCampaigns = async () => {
      try {
        await dispatch(getActiveCampaigns()).unwrap();
      } catch (error) {
        console.error("Failed to fetch active campaigns:", error);
      } finally {
        setHasFetchedCampaigns(true);
      }
    };

    fetchCampaigns();
  }, [dispatch, activeCampaigns]);

  const campaignsLoading =
    !hasFetchedCampaigns ||
    (typeof activeCampaignsLoading === "boolean"
      ? activeCampaignsLoading &&
        (!activeCampaigns || activeCampaigns.length === 0)
      : isLoading &&
        (!Array.isArray(activeCampaigns) || activeCampaigns.length === 0));

  const formatDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatObjective = (objective) => {
    const objectiveMap = {
      OUTCOME_LEADS: "Leads",
      OUTCOME_SALES: "Sales",
      OUTCOME_TRAFFIC: "Traffic",
      OUTCOME_ENGAGEMENT: "Engagement",
      OUTCOME_AWARENESS: "Awareness",
      OUTCOME_APP_PROMOTION: "App Promotion",
    };

    return objectiveMap[objective] || objective || "—";
  };

  const renderCampaignSkeletons = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {[1].map((item) => (
        <div
          key={item}
          className="agent-campaign-skeleton-card bg-white rounded shadow-sm"
          style={{
            width: "100%",
            border: "1px solid #f0f0f0",
            padding: "18px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div
            className="agent-campaign-skeleton-header"
            style={{ minWidth: 240, flex: "1 1 60px" }}
          >
            <Skeleton height={22} width="70%" />
          </div>

          <div
            className="agent-campaign-skeleton-stats"
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 12,
              flexWrap: "wrap",
              flex: "3 1 70px",
            }}
          >
            {[1, 2, 3, 4].map((box) => (
              <div
                key={box}
                className="agent-campaign-skeleton-stat"
                style={{
                  background: "#fafafa",
                  borderRadius: 8,
                  padding: "10px 14px",
                  minWidth: 100,
                  flex: "1 1 140px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  justifyContent: "center",
                }}
              >
                <Skeleton height={12} width="60%" />
                <Skeleton height={18} width="80%" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="active__campaigns-wrapper">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 15,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <h3 style={{ margin: 0 }}>Active Campaigns</h3>

        {!campaignsLoading &&
          Array.isArray(activeCampaigns) &&
          activeCampaigns.length > 0 && (
            <span
              style={{
                background: "#fff7e6",
                color: "#d46b08",
                border: "1px solid #ffd591",
                borderRadius: "999px",
                padding: "4px 12px",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {activeCampaigns.length} Running
            </span>
          )}
      </div>

      {campaignsLoading ? (
        renderCampaignSkeletons()
      ) : Array.isArray(activeCampaigns) && activeCampaigns.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginBottom: 20,
          }}
        >
          {activeCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded shadow-sm"
              style={{
                width: "100%",
                border: "1px solid #f0f0f0",
                padding: "18px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 240, flex: "1 1 60px" }}>
                <h5
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: "#1f1f1f",
                    lineHeight: 1.4,
                  }}
                >
                  {campaign.name || "Unnamed Campaign"}
                </h5>
                <small style={{ color: "#8c8c8c" }}>
                  Campaign ID: {campaign.id}
                </small>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 12,
                  flexWrap: "wrap",
                  flex: "3 1 70px",
                }}
              >
                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Objective
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {formatObjective(campaign.objective)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Running Ad Sets
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {campaign.running_adsets ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Start Date
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {formatDate(campaign.schedule_start)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    End Date
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {campaign.schedule_end
                      ? formatDate(campaign.schedule_end)
                      : "Ongoing"}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Campaign Status
                  </small>
                  <div
                    style={{
                      color:
                        campaign.status === "ACTIVE" ? "#389e0d" : "#8c8c8c",
                      fontWeight: 600,
                    }}
                  >
                    {campaign.status || "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="bg-white rounded shadow-sm"
          style={{
            border: "1px solid #f0f0f0",
            padding: "30px 20px",
            marginBottom: 20,
          }}
        >
          <Empty description="No active campaigns found" />
        </div>
      )}
    </div>
  );
};

export default AgentsActiveCampaigns;